import {effect, inject, Injectable, untracked} from '@angular/core';
import {DatosApp, Alumno, Token, SyncDeltas} from '@kilobytelab/types';
import {StorageService, UiService} from '@kilobytelab/core';
import {apellidosHelper, ApiAppService, ApiMiscService} from '@kilobytelab/domain';
import {LocalStorageService} from './storage.service';
import {AgendaService} from './agenda.service';
import {CircularesService} from './circulares.service';
import {InformesService} from './informes.service';
import {AvisosService} from './avisos.service';
import {ProgramacionService} from './prog.service';
import {AppService} from './app.service';

@Injectable({
  providedIn: 'root'
})
export class SyncService {

  private apiApp = inject(ApiAppService);
  private apiMisc = inject(ApiMiscService);
  private storage = inject(StorageService);
  private localstorage = inject(LocalStorageService);
  private agendaService = inject(AgendaService);
  private circService = inject(CircularesService);
  private informesService = inject(InformesService);
  private avisosService = inject(AvisosService);
  private progService = inject(ProgramacionService);
  private appService = inject(AppService);
  private uiService = inject(UiService);

  constructor() {
    document.addEventListener('visibilitychange', async () => {
      if (document.visibilityState === 'visible') {
        const token: Token = await this.storage.getToken();
        if (token) await this.sync();
      }
    });
    effect(() => {
      const trigger = this.appService.pushRecibidoTrigger();
      if (trigger > 0) {
        untracked(() => {
          this.sync()
        })
      }
    })
  }

  private syncTask: Promise<void> = null;

  sync(arranqueForzado: boolean = false): Promise<void> {
    if (this.syncTask) {
      return this.syncTask
    }
    this.syncTask = this.ejecutarSync(arranqueForzado).finally(() => {this.syncTask = null});
    return this.syncTask
  }

  private async ejecutarSync(arranqueForzado: boolean) {
    try {
      const [status, local, padres, padre, fechas] = await Promise.all([
        this.apiApp.getStatus(),
        this.localstorage.getSyncDeltas(),
        this.localstorage.getPadres(),
        this.localstorage.getPadre(),
        this.localstorage.getFechas()
      ]);
      if (!fechas) {
        await this.localstorage.setFechas({agendas: null, avisos: null});
        this.appService.fechas.set({agendas: null, avisos: null})
      } else {
        this.appService.fechas.set(fechas)
      }
      if (!padres || !padre) {
        await this.initDataPadre()
      } else {
        this.appService.PADRES.set(padres);
        this.appService.PADRE.set(padre);
        await this.setActual()
      }
      this.appService.matriculaDisponible.set(status.matriculas);
      this.appService.streamDisponible.set(status.stream);
      if (arranqueForzado || !local) {
        if (status) await this.localstorage.setSyncDeltas(status);
        return this.firstTimeLogin()
      }
      const peticionesPendientes: Promise<any>[] = [];
      const guardado: SyncDeltas = {...local};
      if (status.perfil > guardado.perfil) {
        peticionesPendientes.push(this.initDataPadre())
      }
      if (status.modificado > guardado.modificado) {
        peticionesPendientes.push(this.checkApp())
      } else if (!this.appService.APP()) {
        const app: DatosApp = await this.storage.getApp();
        this.appService.APP.set(app)
      }
      this.appService.appLista.set(true);
      if (status.aviso > guardado.aviso) {
        peticionesPendientes.push(this.avisosService.checkAvisos())
      }
      if (status.agenda > guardado.agenda) {
        peticionesPendientes.push(this.agendaService.checkAgenda())
      }
      if (status.informe > guardado.informe) {
        peticionesPendientes.push(this.informesService.checkInformes())
      }
      if (status.circular > guardado.circular) {
        peticionesPendientes.push(this.circService.checkCirculares())
      }
      if (status.proyecto > guardado.proyecto) {
        peticionesPendientes.push(this.progService.checkProgramacion())
      }
      if (peticionesPendientes.length > 0) {
        await Promise.all(peticionesPendientes);
        return this.localstorage.setSyncDeltas(status)
      }
    } catch (err) {
      throw err
    }
  }

  async initDataPadre() {
    try {
      const alumnosCompleto = await this.apiApp.getAlumnosCompleto();
      const padre1 = apellidosHelper(alumnosCompleto[1]);
      const padre2 = apellidosHelper(alumnosCompleto[2]);
      this.appService.PADRES.set([padre1, padre2]);
      this.localstorage.setPadres([padre1, padre2]).catch(console.error);
      await this.updatePadre(alumnosCompleto[0]);
      await this.setActual()
    } catch (err) {
      throw err
    }
  }

  private async updatePadre(alumnos: Alumno[]) {
    this.localstorage.setPadre(alumnos).catch(console.error);
    this.appService.PADRE.update(actuales => {
      return alumnos.map(nuevo => {
        const existente = actuales.find(a => a.id === nuevo.id);
        if (existente) return {...existente, ...nuevo};
        return nuevo
      })
    })
  }

  setActual = async () => {
    const hijos = this.appService.PADRE();
    if (!hijos?.length) {
      this.appService.indiceAlumno.set(null);
      return
    }
    const indiceGuardado = await this.localstorage.getAlumnoActual();
    if (indiceGuardado !== null && indiceGuardado !== undefined) {
      this.appService.indiceAlumno.set(indiceGuardado);
      return
    }
    const alumnosAlta: Alumno[] = hijos.filter(a => a.estado !== 'baja' && a.estado !== 'matriculado');
    const indice = alumnosAlta.length > 0 ? hijos.findIndex(a => a.id === alumnosAlta[0].id) : 0;
    this.appService.indiceAlumno.set(indice);
    this.localstorage.setAlumnoActual(indice).catch(console.error)
  }

  private async firstTimeLogin() {
    await this.checkApp();
    const resultados = await Promise.allSettled([
      this.avisosService.checkAvisos(),
      this.agendaService.checkAgenda(),
      this.circService.checkCirculares(),
      this.informesService.checkInformes(),
      this.progService.checkProgramacion()
    ]);
    const fallos = resultados.filter(r => r.status === 'rejected');
    if (fallos.length > 0) {
      console.warn('FirstTimeLogin: Algunos módulos fallaron al cargar, pero la app sigue viva.', fallos);
      await this.uiService.mostrarToast('Algunos datos podrían no estar actualizados', true)
    }
    this.appService.appLista.set(true)
  }

  private async checkApp() {
    try {
      const app: DatosApp = await this.apiMisc.getDatosApp();
      this.appService.APP.update(datosApp => ({...datosApp, ...app}));
      return this.storage.setApp(app)
    } catch (err) {
      const app: DatosApp = await this.storage.getApp();
      if (!app) return this.uiService.mostrarToast('Error recuperando los datos del Centro', true);
      this.appService.APP.set(app)
    }
  }
}
